import React from "react"

import dayjs from "dayjs"
import i18n from "i18next"
import detector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"

import { DEFAULT_NS, WHITELABEL_NS } from "./constants"
import "dayjs/locale/de"
import "dayjs/locale/es"
import "dayjs/locale/fr"
import "dayjs/locale/is"
import "dayjs/locale/it"
import "dayjs/locale/nl"
import "dayjs/locale/pl"
import "dayjs/locale/pt"
import flags from "react-phone-number-input/flags"

export const DEFAULT_LANGUAGE = "en"

export const LANGUAGE_STORAGE_KEY = "app_language"

export const AVAILABLE_LANGUAGES = [
  "en",
  "es",
  "de",
  "nl",
  "fo",
  "fr",
  "is",
  "it",
  "pl",
  "pt",
  "sl",
]

// For visitor management
export const VM_LANGUAGES = AVAILABLE_LANGUAGES

// For web
export const WEB_LANGUAGES = AVAILABLE_LANGUAGES.filter(
  (lang) => lang !== "fo" && lang !== "sl",
)

export const MOBILE_LANGUAGES = [...WEB_LANGUAGES, "sl"]

/**
 * Get the supported language from the language code.
 * @param language - The language code to get the supported language for.
 * @param supportedLanguages - The supported languages to check against.
 * @returns The supported language or the default language if the language is not supported.
 */
export const getSupportedLanguage = (
  language: string,
  supportedLanguages: string[] = AVAILABLE_LANGUAGES,
) => {
  const shortLanguage = language.split("-")[0]

  if (supportedLanguages.includes(shortLanguage)) {
    return shortLanguage
  }

  return DEFAULT_LANGUAGE
}

export const ORGANIZATION_LANGUAGE_OPTIONS = [
  { label: "English", value: "en" },
  { label: "Deutsch", value: "de" },
  { label: "Español", value: "es" },
  { label: "Français", value: "fr" },
  { label: "Íslenska", value: "is" },
  { label: "Italiano", value: "it" },
  { label: "Nederlands", value: "nl" },
  { label: "Polski", value: "pl" },
  { label: "Português", value: "pt" },
  { label: "Føroyskt", value: "fo" },
  { label: "Slovenščina", value: "sl" },
]

export const WEB_LANGUAGE_OPTIONS = ORGANIZATION_LANGUAGE_OPTIONS.filter(
  ({ value }) => value !== "fo" && value !== "sl",
)

export const MOBILE_LANGUAGE_OPTIONS = ORGANIZATION_LANGUAGE_OPTIONS.filter(
  ({ value }) => MOBILE_LANGUAGES.includes(value),
)

const createFlagWrapper = (FlagComponent: React.FC<{ title: string }>) => () =>
  React.createElement(FlagComponent)

export const LANGUAGE_ICONS: Record<string, React.FC> = {
  es: createFlagWrapper(flags.ES!),
  en: createFlagWrapper(flags.GB!),
  de: createFlagWrapper(flags.DE!),
  nl: createFlagWrapper(flags.NL!),
  fr: createFlagWrapper(flags.FR!),
  is: createFlagWrapper(flags.IS!),
  it: createFlagWrapper(flags.IT!),
  pl: createFlagWrapper(flags.PL!),
  pt: createFlagWrapper(flags.PT!),
  fo: createFlagWrapper(flags.FO!),
  sl: createFlagWrapper(flags.SI!),
}

// Lazy load translation function
const loadLanguageResources = async (language: string) => {
  const [defaultTranslation, whitelabelTranslation] = await Promise.all([
    import(`./assets/i18n/${language}/${language}.json`),
    import(`./assets/i18n/${language}/${language}-whitelabel.json`),
  ])

  return {
    [DEFAULT_NS]: defaultTranslation.default,
    [WHITELABEL_NS]: whitelabelTranslation.default,
  }
}

// Initialize i18next with lazy loading backend
i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: {}, // Start with empty resources
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      /**
       * Priority chain: explicit user choice (`localStorage.app_language`) →
       * device system locale (`navigator.language`) → `fallbackLng`.
       * `caches: ["localStorage"]` writes the resolved language back to
       * the same key on every `changeLanguage` call so a freshly-installed
       * app picks up the device locale once and then keeps the value
       * consistent with what `LanguageSelector` and `GeneralForm` read.
       */
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    // Prevent i18next from loading missing translations
    partialBundledLanguages: true,
  })

// Add custom formatter for "a" and "an"
i18n.services.formatter?.add(
  "enHandleAn",
  (value: any, lng: string | undefined) => {
    if (!lng?.startsWith("en")) {
      return value
    }

    if (typeof value === "string") {
      const letters = ["a", "e", "i", "o", "u", "h"]
      if (letters.includes(value.toLocaleLowerCase()[0])) {
        return `an ${value}`
      }

      return `a ${value}`
    }

    return value
  },
)

// Load initial language
let langShort = getSupportedLanguage(i18n.language)

// Load the initial language resources
loadLanguageResources(langShort).then((resources) => {
  i18n.addResourceBundle(
    langShort,
    DEFAULT_NS,
    resources[DEFAULT_NS],
    true,
    true,
  )
  i18n.addResourceBundle(
    langShort,
    WHITELABEL_NS,
    resources[WHITELABEL_NS],
    true,
    true,
  )
  i18n.changeLanguage(langShort)
  dayjs.locale(langShort)
})

// Handle language changes - load new language if not already loaded
i18n.on("languageChanged", async (lng) => {
  const updatedLangShort = lng.split("-")[0]

  // Update dayjs locale
  dayjs.locale(updatedLangShort)

  // Check if language resources are already loaded
  if (i18n.hasResourceBundle(updatedLangShort, DEFAULT_NS)) {
    return
  }

  try {
    const resources = await loadLanguageResources(updatedLangShort)
    i18n.addResourceBundle(
      updatedLangShort,
      DEFAULT_NS,
      resources[DEFAULT_NS],
      true,
      true,
    )
    i18n.addResourceBundle(
      updatedLangShort,
      WHITELABEL_NS,
      resources[WHITELABEL_NS],
      true,
      true,
    )
  } catch (error) {
    console.error(
      `Failed to load language resources for ${updatedLangShort}:`,
      error,
    )
  }
})

/**
 * Change language with proper resource loading.
 * This ensures resources are loaded before the language change completes.
 */
export const changeAppLanguage = async (lng: string): Promise<void> => {
  const langShort = lng.split("-")[0]

  // Load resources first if not already loaded
  if (!i18n.hasResourceBundle(langShort, DEFAULT_NS)) {
    try {
      const resources = await loadLanguageResources(langShort)
      i18n.addResourceBundle(
        langShort,
        DEFAULT_NS,
        resources[DEFAULT_NS],
        true,
        true,
      )
      i18n.addResourceBundle(
        langShort,
        WHITELABEL_NS,
        resources[WHITELABEL_NS],
        true,
        true,
      )
    } catch (error) {
      console.error(
        `Failed to load language resources for ${langShort}:`,
        error,
      )
      return
    }
  }

  // Now change the language - resources are already loaded
  await i18n.changeLanguage(langShort)
  dayjs.locale(langShort)
}

export default i18n
